const STORAGE_KEY = 'saf:post-login-return';
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

interface StoredPostLoginIntent {
  path: string;
  createdAt: number;
}

export function toSameOriginPath(candidate: string): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  if (!candidate.trim()) {
    return null;
  }

  try {
    const url = new URL(candidate, window.location.origin);
    if (url.origin !== window.location.origin) {
      return null;
    }
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
}

export function rememberPostLoginReturnUrl(candidate: string): void {
  const path = toSameOriginPath(candidate);
  if (!path) {
    return;
  }

  try {
    const value: StoredPostLoginIntent = { path, createdAt: Date.now() };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Storage can be unavailable in private browsing. The normal login fallback still works.
  }
}

export function getPostLoginReturnUrl(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const value = JSON.parse(raw) as StoredPostLoginIntent;
    if (
      !value ||
      typeof value.path !== 'string' ||
      typeof value.createdAt !== 'number' ||
      Date.now() - value.createdAt > MAX_AGE_MS
    ) {
      clearPostLoginReturnUrl();
      return null;
    }

    const path = toSameOriginPath(value.path);
    if (!path) {
      clearPostLoginReturnUrl();
      return null;
    }
    return path;
  } catch {
    clearPostLoginReturnUrl();
    return null;
  }
}

export function clearPostLoginReturnUrl(): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore unavailable storage.
  }
}
